import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Plus, Sparkles } from "lucide-react";
import { PageHero, SectionHead } from "@/components/PageHero";
import { Reveal } from "@/components/anim";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { useCart } from "@/lib/cart";

const VENDOR_INITIAL = {
  name: "",
  brand: "",
  category: CATEGORIES[0],
  price: "",
  description: "",
  image_url: "",
  vendor_name: "",
  vendor_email: "",
  vendor_instagram: "",
};

function ProductCard({ p, i }) {
  const { add } = useCart();
  return (
    <Reveal delay={(i % 4) * 0.06}>
      <div
        className="group flex h-full flex-col overflow-hidden rounded-3xl border border-beige/60 bg-white/80 shadow-[0_4px_24px_rgba(26,24,23,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(26,24,23,0.12)]"
        data-testid={`product-card-${i + 1}`}
      >
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={p.image_url}
            alt={p.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col p-6">
          <p className="text-[10px] uppercase tracking-[0.22em] text-fog">{p.brand}</p>
          <h3 className="mt-1 font-serif text-xl leading-tight text-ink">{p.name}</h3>
          <p className="mt-2 flex-1 text-xs leading-relaxed text-clay line-clamp-2">{p.description}</p>
          <div className="mt-5 flex items-center justify-between gap-3">
            <p className="font-serif text-2xl text-ink">${Number(p.price).toFixed(2)}</p>
            <button
              onClick={() => add(p)}
              data-testid={`add-to-cart-${i + 1}`}
              className="btn btn-dark !px-5 !py-2.5 !text-[10px]"
            >
              <Plus size={13} strokeWidth={1.5} />
              Add to Bag
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function VendorForm() {
  const [form, setForm] = useState(VENDOR_INITIAL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price) });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const f = (key, props = {}) => ({
    className: "field",
    value: form[key],
    onChange: set(key),
    ...props,
  });

  if (done) {
    return (
      <div className="rounded-3xl border border-blushdeep bg-blush/50 p-10 text-center" data-testid="vendor-success">
        <Sparkles size={24} strokeWidth={1.5} className="mx-auto text-ink" />
        <p className="mt-4 font-serif text-2xl uppercase text-ink">
          Submitted <em>for review.</em>
        </p>
        <p className="mt-3 text-sm text-clay">
          Thank you! Our team will review your item and approve it shortly. Approved items appear
          in the shop the moment they&rsquo;re live.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="vendor-form">
      <div>
        <label className="lbl" htmlFor="v-brand">Brand / Business Name *</label>
        <input id="v-brand" required {...f("brand")} data-testid="vendor-brand-input" />
      </div>
      <div>
        <label className="lbl" htmlFor="v-name">Product Name *</label>
        <input id="v-name" required {...f("name")} data-testid="vendor-name-input" />
      </div>
      <div>
        <label className="lbl" htmlFor="v-category">Category *</label>
        <select id="v-category" {...f("category")} data-testid="vendor-category-select">
          {CATEGORIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="lbl" htmlFor="v-price">Price (USD) *</label>
        <input id="v-price" required type="number" min="0.5" step="0.01" {...f("price")} data-testid="vendor-price-input" />
      </div>
      <div>
        <label className="lbl" htmlFor="v-vendor">Your Name *</label>
        <input id="v-vendor" required {...f("vendor_name")} data-testid="vendor-vendor-name-input" />
      </div>
      <div>
        <label className="lbl" htmlFor="v-email">Email *</label>
        <input id="v-email" required type="email" {...f("vendor_email")} data-testid="vendor-email-input" />
      </div>
      <div>
        <label className="lbl" htmlFor="v-ig">Instagram</label>
        <input id="v-ig" placeholder="@yourbrand" {...f("vendor_instagram")} data-testid="vendor-instagram-input" />
      </div>
      <div>
        <label className="lbl" htmlFor="v-img">Product Image URL *</label>
        <input id="v-img" required type="url" placeholder="https://…" {...f("image_url")} data-testid="vendor-image-input" />
      </div>
      <div className="sm:col-span-2">
        <label className="lbl" htmlFor="v-desc">Description</label>
        <textarea id="v-desc" rows={3} {...f("description")} data-testid="vendor-description-input" />
      </div>
      {error && <p className="sm:col-span-2 text-sm text-red-700" data-testid="vendor-form-error">{error}</p>}
      <div className="sm:col-span-2">
        <button type="submit" disabled={busy} className="btn btn-dark w-full sm:w-auto disabled:opacity-60" data-testid="vendor-submit-button">
          {busy ? "Submitting…" : "Submit Your Product"}
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </button>
      </div>
    </form>
  );
}

export default function Shop() {
  const [category, setCategory] = useState("All");
  const [products, setProducts] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const url = category === "All" ? "/products" : `/products?category=${encodeURIComponent(category)}`;
        const { data } = await api.get(url);
        setProducts(data);
      } catch (e) {
        setError(formatApiError(e));
      }
    };
    load();
  }, [category]);

  const heading = useMemo(
    () => (category === "All" ? "Every brand under one roof." : category),
    [category]
  );

  return (
    <>
      <PageHero
        eyebrow="Shop Here"
        lines={[
          <>Bermuda Brands.</>,
          <>
            <em>Brighter Together.</em>
          </>,
        ]}
        sub="Retail from the brands who live inside Essandtials — buy online, then pick up in store at 39 Dundonald Street or have it delivered. Online and in-store, all one boutique."
      />

      <section className="bg-paper pb-24 sm:pb-32" data-testid="shop-page">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="flex flex-col justify-between gap-5 border-b border-beige/60 pb-6 sm:flex-row sm:items-center">
            <p className="font-serif text-2xl italic text-ink" data-testid="shop-heading">
              {heading}
            </p>
            <div className="flex items-center gap-3">
              <label htmlFor="category" className="text-[11px] uppercase tracking-[0.22em] text-fog">
                Category
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="field !w-56"
                data-testid="category-filter"
              >
                <option value="All">All Categories</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && <p className="mt-8 text-sm text-red-700">{error}</p>}
          {products === null && !error && <p className="mt-10 text-sm text-fog">Loading the boutique…</p>}
          {products && products.length === 0 && (
            <p className="mt-10 rounded-3xl border border-beige/60 bg-white/70 p-10 text-center text-sm text-clay" data-testid="shop-empty">
              No items in this category yet — check back soon, our brands restock often.
            </p>
          )}
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products && products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
          </div>
        </div>
      </section>

      <section className="bg-sand py-24 sm:py-32" data-testid="vendor-section">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <SectionHead
            eyebrow="Sell with us"
            title={
              <>
                Sell in store.
                <br />
                <em>Sell here too.</em>
              </>
            }
            sub="Already an Essandtials brand? List your retail items in our online shop — your products live in-store and online at the same time. Submit an item and we'll review it within a couple of days."
          />
          <Reveal delay={0.2}>
            <div className="mt-12 rounded-3xl border border-beige/60 bg-white/80 p-8 sm:p-10">
              <VendorForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
